import React, { useEffect, useMemo, useState } from 'react';
import { get } from '@/features/shared/lib/api';
import axios from 'axios';
import ResearcherIdIdsPanel from '@/features/mypage/components/edit/ResearcherIdIdsPanel';

// ResearcherId
// - New record-driven read-only list (kept)
// - PLUS the existing legacy HTML area restored for future API wiring
export default function ResearcherId({ record, showLegacyArea = true } = {}) {
  const r = record || {};

  // Base values for the fixed items (editable via user API)
  const initialBaseValues = useMemo(() => ({
    school_no: r.school_no ?? '',
    researcher_id: r.researcher_id ?? '',
    scopus_id: r.scopus_id ?? '',
    kci_id: r.kci_id ?? '',
    kri_id: r.kri_id ?? '',
    orcid: r.orcid ?? '',
    researchgate_id: r.researchgate_id ?? '',
    isni: r.isni ?? '',
    googlescholar_id: r.googlescholar_id ?? '',
  }), [r]);
  const [baseValues, setBaseValues] = useState(initialBaseValues);
  useEffect(() => {
    setBaseValues(initialBaseValues);
  }, [initialBaseValues]);

  // Map each ID type to label, key, and value from `baseValues`
  const items = useMemo(() => ([
    { label: '교직원 번호(학번)', key: 'school_no', value: baseValues.school_no },
    { label: 'Researcher ID(WoS)', key: 'researcher_id', value: baseValues.researcher_id },
    { label: 'Scopus ID', key: 'scopus_id', value: baseValues.scopus_id },
    { label: 'KCI ID', key: 'kci_id', value: baseValues.kci_id },
    { label: 'KRI ID', key: 'kri_id', value: baseValues.kri_id },
    { label: 'ORCID', key: 'orcid', value: baseValues.orcid },
    { label: 'ResearchGate ID', key: 'researchgate_id', value: baseValues.researchgate_id },
    { label: 'ISNI', key: 'isni', value: baseValues.isni },
    { label: 'GoogleScholar ID', key: 'googlescholar_id', value: baseValues.googlescholar_id },
  ]), [baseValues]);

  // Options for the legacy add-area (fetched from code API) with safe fallback
  const addOptionsFallback = [
    { value: '', label: '선택해주세요.' },
    { value: 'googlescholar_id', label: 'Google Scholar ID' },
    { value: 'researchgate_id', label: 'ResearchGate ID' },
    { value: 'isni', label: 'ISNI ID' },
    { value: 'orcid', label: 'ORCID' },
    { value: 'kci_id', label: 'KCI ID' },
    { value: 'kri_id', label: 'KRI ID' },
    { value: 'researcher_id', label: 'Researcher ID' },
    { value: 'rtap_id', label: 'RTAP ID' },
    { value: 'scopus_id', label: 'Scopus ID' },
    { value: 'authority_id', label: '저자전거' },
  ];
  const [addOptions, setAddOptions] = useState(addOptionsFallback);
  const [optLoading, setOptLoading] = useState(false);
  const [optError, setOptError] = useState('');

  // Load addOptions from code API (rm/v1/code/search/scholar_id_type)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // setOptLoading(true);
        // setOptError('');
        const res = await get('/rm/v1/code/search/scholar_id_type');
        const records = res?.result?.records || res?.records || res?.result || [];
        const mapped = Array.isArray(records)
          ? records
              .filter((it) => it && (it.disp_yn || 'Y') === 'Y' && (it.del_yn || 'N') !== 'Y' && it.code_id && it.code_id !== 'school_no')
              .sort((a, b) => (Number(a?.visible_seq) || 9999) - (Number(b?.visible_seq) || 9999))
              .map((it) => ({
                value: it.code_id,
                label: it.code_name || it.code_name_eng || it.code_id,
                map_code: it.map_code || '',
              }))
          : [];
        if (!alive) return;
        setAddOptions(mapped.length ? [{ value: '', label: '선택해주세요.' }, ...mapped] : addOptionsFallback);
      } catch (e) {
        if (!alive) return;
        setOptError(e?.response?.data?.message || e?.message || '코드 목록을 불러오지 못했습니다.');
        setAddOptions(addOptionsFallback);
      } finally {
        // if (alive) setOptLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // For rendering labels in added list when row.label is missing
  const labelMap = useMemo(() => {
    const map = {};
    (addOptions || []).forEach((opt) => {
      if (opt && opt.value) map[opt.value] = opt.label;
    });
    return map;
  }, [addOptions]);

  // Added IDs list from backend (record.scholar_id_type)
  const [list, setList] = useState(Array.isArray(r.scholar_id_type) ? r.scholar_id_type : []);
  useEffect(() => {
    setList(Array.isArray(record?.scholar_id_type) ? record.scholar_id_type : []);
  }, [record]);

  // State for the legacy "추가하기" area (now wired to API)
  const [addType, setAddType] = useState('');
  const [addValue, setAddValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Refresh key for reloading added list component after adds from the form
  const [refreshKey, setRefreshKey] = useState(0);

  // Edit states: added list rows (base inputs are always editable)
  const [editingRowId, setEditingRowId] = useState(null);
  const [editRowValue, setEditRowValue] = useState('');

  const addDisabled = submitting || !addType || !addValue.trim();

  // Axios multipart/form-data helper for WP REST
  const wpHeaders = useMemo(() => ({
    'X-WP-Nonce': typeof rmVars !== 'undefined' ? rmVars.api_nonce : '',
    'Content-Type': 'multipart/form-data',
  }), []);

  // NOTE: 아래/위 영역 API가 달라 혼선을 막기 위해 단일 엔드포인트로 통일
  // /wp-json + (baseUrl without trailing /rm/v1) + /rm/v1/my_library/info
  // const baseUrl = '/wp-json/rm/v1';
  // const finalInfoUrl = baseUrl.replace(/\/rm\/v1\/?$/, '') + '/rm/v1/my_library/info';
  const baseUrl = typeof rmVars !== 'undefined' ? rmVars.restApi : '/scholar/wp-json';
  const finalUrl = baseUrl.replace(/\/rm\/v1\/?$/, '') + '/rm/v1/my_library/info';

  const mpPost = async (action, payload) => {
    const formDataToSend = new FormData();
    // 서버에서 분기할 수 있도록 action 힌트 포함 (하단 "추가된 Researcher ID 리스트" 전용)
    if (action) formDataToSend.append('action', action);
    Object.keys(payload || {}).forEach((key) => {
      const val = payload[key];
      formDataToSend.append(key, val == null ? '' : val);
    });
    const response = await axios.post(finalUrl, formDataToSend, { headers: wpHeaders });
    return response?.data;
  };

  // 상단 고정 영역(기본 필드) 전용: key:value 형태로 전송 (예: { isni: "0000...", record_id: 1684 })
  const postInfoKV = async (kvPayload) => {
    const formDataToSend = new FormData();
    Object.keys(kvPayload || {}).forEach((key) => {
      const val = kvPayload[key];
      formDataToSend.append(key, val == null ? '' : val);
    });
    const response = await axios.post(finalUrl, formDataToSend, { headers: wpHeaders });
    return response?.data;
  };

  const onAdd = async () => {
    if (addDisabled) return;
    if (!r?.scholar_id) {
      setErrorMsg('유효하지 않은 사용자입니다. (scholar_id 없음)');
      return;
    }
    try {
      setSubmitting(true);
      setErrorMsg('');
      const payload = { id_type: addType, id_value: addValue.trim() };
      const res = await mpPost('insert', payload);
      const next = res?.result?.scholar_id_type || res?.scholar_id_type || res?.result || [];
      if (Array.isArray(next)) {
        setList(next);
      } else if (next && Array.isArray(next.scholar_id_type)) {
        setList(next.scholar_id_type);
      }
      setAddValue('');
      // keep type selected for quick multiple adds
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || '추가에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (row) => {
    if (!r?.scholar_id || !row?.ID) return;
    try {
      setSubmitting(true);
      setErrorMsg('');
    // , record_id: r.scholar_id
      const payload = { ID: row.ID };
      const res = await mpPost('delete', payload);
      const next = res?.result?.scholar_id_type || res?.scholar_id_type || res?.result || [];
      if (Array.isArray(next)) {
        setList(next);
      } else if (next && Array.isArray(next.scholar_id_type)) {
        setList(next.scholar_id_type);
      } else {
        // Fallback: remove locally
        setList(prev => prev.filter(it => it.ID !== row.ID));
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || '삭제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // Helpers
  const findExistingByType = (key) => (Array.isArray(list) ? list.find(it => it?.id_type === key) : null);

  // Save base (top) item edit — send as key:value to /my_library/info
  const onSaveBase = async (key, overrideValue = undefined) => {
    const prevVal = baseValues[key] || '';
    const raw = overrideValue !== undefined ? overrideValue : prevVal;
    const value = (raw || '').trim();
    if (!r?.scholar_id) {
      setErrorMsg('유효하지 않은 사용자입니다. (scholar_id 없음)');
      return;
    }
    try {
      setSubmitting(true);
      setErrorMsg('');
      // Build key:value payload expected by update_my_library_info
      const kv = { record_id: r.scholar_id };
      kv[key] = value;
      const resp = await postInfoKV(kv);
      if (resp?.status === true) {
        // 성공 시에만 반영
        setBaseValues(prev => ({ ...prev, [key]: value }));
        // Maintain list consistency: if this key exists in added list, update local list value too
        setList(prev => {
          if (!Array.isArray(prev)) return prev;
          return prev.map(it => (it?.id_type === key ? { ...it, id_value: value } : it));
        });
      } else {
        // 실패 시 기존 값 유지
        setErrorMsg(resp?.message || '저장에 실패했습니다. (status=false)');
        setBaseValues(prev => ({ ...prev, [key]: prevVal }));
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || '저장에 실패했습니다.');
      // 실패 시 기존 값 유지
      setBaseValues(prev => ({ ...prev, [key]: prevVal }));
    } finally {
      setSubmitting(false);
    }
  };

  // Save added row edit
  const onSaveRow = async (row) => {
    const value = (editRowValue || '').trim();
    if (!r?.scholar_id || !row?.ID) return;
    if (value === (row?.id_value || '')) {
      setEditingRowId(null);
      setEditRowValue('');
      return;
    }
    try {
      setSubmitting(true);
      setErrorMsg('');
      const res = await mpPost('update', { ID: row.ID, id_value: value, record_id: r.scholar_id });
      const next = res?.result?.scholar_id_type || res?.scholar_id_type || res?.result || [];
      if (Array.isArray(next)) {
        setList(next);
      } else if (next && Array.isArray(next.scholar_id_type)) {
        setList(next.scholar_id_type);
      } else {
        // Fallback: update locally
        setList(prev => prev.map(it => it.ID === row.ID ? { ...it, id_value: value } : it));
      }
      // Also update base value if same type
      if (row?.id_type && baseValues[row.id_type] != null) {
        setBaseValues(prev => ({ ...prev, [row.id_type]: value }));
      }
      setEditingRowId(null);
      setEditRowValue('');
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || '수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center w-full">
        <h3 className="mypage-subpage-title text-[26px] lg:text-[24px] font-black lg:font-bold leading-[130%] lg:leading-[100%]">Researcher ID</h3>
      </div>

      <div className="mypage-card mt-[16px] lg:mt-6 lg:p-[60px] bg-white rounded-[12px]">

        <div className="flex flex-col gap-4">{items.map((item) => {
            const isReadOnly = item.key === 'school_no';
            return (
              <div key={item.key} className="flex gap-1 xl:gap-3">
                <div className="flex-shrink-0 w-[140px] xl:w-[204px]">
                  <select
                    value={item.label}
                    disabled
                    className="w-full h-[44px] px-4 pr-[40px] border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none appearance-none text-line-1 bg-[#F3F5F9] cursor-default"
                    style={{
                      backgroundImage: `url(${typeof rmVars !== 'undefined' ? rmVars.themeUrl : ''}images/expand_more_6C7687.png)`,
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '16px',
                      backgroundPosition: 'calc(100% - 16px)',
                    }}
                  >
                    <option value="" disabled hidden>Select</option>
                    <option value={item.label}>{item.label}</option>
                  </select>
                </div>

                <div className={isReadOnly ? 'flex-1 min-w-0' : 'w-[calc(100%-192px)] xl:w-[calc(100%-286px)]'}>
                  {isReadOnly ? (
                    <div className="flex items-center h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] bg-[#F3F5F9] text-[#6C7687] w-full">
                      {baseValues[item.key] || ''}
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="w-full h-[44px] px-4 border border-[#E4E8F1] rounded-[8px] text-[13px] placeholder-[#B4BCCC] outline-none bg-white"
                      value={baseValues[item.key] || ''}
                      onChange={(e) => setBaseValues(prev => ({ ...prev, [item.key]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSaveBase(item.key);
                      }}
                    />
                  )}
                </div>

                {!isReadOnly && (
                  <>
                    <div className="flex-shrink-0 w-[44px] xl:w-[58px] h-[44px] bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px]">
                      <button
                        type="button"
                        className={`flex justify-center items-center w-full h-full ${submitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        onClick={(e) => { e.preventDefault(); if (!submitting) onSaveBase(item.key); }}
                        disabled={submitting}
                      >
                        <span className="text-[14px] font-medium text-[#6C7687]">저장</span>
                      </button>
                    </div>
                    <div className="flex-shrink-0 w-[44px] xl:w-[58px] h-[44px] bg-[#F3F5F9] border border-[#E4E8F1] rounded-[12px]">
                      <button
                        type="button"
                        className={`flex justify-center items-center w-full h-full ${submitting ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (submitting) return;
                          setBaseValues(prev => ({ ...prev, [item.key]: '' }));
                          onSaveBase(item.key, '');
                        }}
                        disabled={submitting}
                      >
                        <img src={typeof rmVars !== 'undefined' ? (rmVars.themeUrl + 'images/trash-ico.png') : ''} className="xl:hidden w-5" alt="delete" />
                        <span className="hidden xl:block text-[14px] font-medium text-[#6C7687]">삭제</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <ResearcherIdIdsPanel record={record} showAdd={showLegacyArea} addOptions={addOptions} optLoading={optLoading} optError={optError} />
      </div>
    </div>
  );
}
